'use client';

import { Heart } from 'lucide-react';
import { createContext, FC, memo, useCallback, useContext, useState } from 'react';

import { ACCENT } from '@shared/constants/colors';
import { ROUTES } from '@shared/constants/routes';
import { useAppDispatch } from '@shared/hooks/redux';
import { HttpStatuses } from '@shared/types/enums/http.enum';
import { BreadcrumbItemType, WithNeedUpdateTokenProps } from '@shared/types/global';
import { ProductCardType, ProductsType } from '@shared/types/product';
import { Container } from '@shared/ui/Container';
import { PageHead } from '@shared/ui/PageHead';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';
import { Empty } from '@widgets/Empty/Empty';
import { ProductCard } from '@widgets/ProductCard';
import { RefreshTokenState } from '@widgets/RefreshTokenState/RefreshTokenState';

import { removeFromFavoritesThunk } from '../model/slice/favoritesSlice';
import cls from './Favorites.module.scss';

type FavoritesType = {
  favoritesProducts: ProductsType;
} & WithNeedUpdateTokenProps;

type FavoritesContextType = {
  removeProduct?: (productId: string) => Promise<void>;
};

const crumbs: BreadcrumbItemType[] = [
  { url: ROUTES.MAIN, label: 'Главная' },
  { url: '', label: 'Избранное' }
];

const FavoritesContext = createContext<FavoritesContextType>({});

export const Favorites: FC<FavoritesType> = memo(({ favoritesProducts, needUpdateToken }) => {
  const dispatch = useAppDispatch();

  const [products, setProducts] = useState<ProductCardType[]>(getProductCardData(favoritesProducts));

  const removeProduct = useCallback(
    async (productId: string) => {
      const { payload } = await dispatch(removeFromFavoritesThunk(productId));

      if (payload !== HttpStatuses.ERROR) {
        setProducts((prevState) => prevState.filter(({ id }) => id !== productId));
      }
    },
    [dispatch]
  );

  return (
    <>
      <FavoritesContext.Provider value={{ removeProduct }}>
        <div className={cls.wrap}>
          <PageHead
            breadcrumbs={crumbs}
            title={'Избранное'}
          />

          {products.length ? (
            <Container>
              <div className={cls.grid}>
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    view={'grid'}
                    data={product}
                  />
                ))}
              </div>
            </Container>
          ) : (
            <Empty
              title={'Нет товаров в избранном'}
              description={'Добавьте интересующие вас товары в избранное'}
              icon={
                <Heart
                  color={ACCENT}
                  size={80}
                />
              }
            />
          )}
        </div>
        <RefreshTokenState
          redirect={false}
          {...{ needUpdateToken }}
        />
      </FavoritesContext.Provider>
    </>
  );
});

export const useFavorites = () => useContext(FavoritesContext);
